import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";
import { allArticles, articleCards } from "@/mocks/articles";
import type { ArticleData } from "@/mocks/articles";

const SITE_URL = "https://allchinabuyspreadsheet.biz";

function RelatedArticles({ currentSlug }: { currentSlug: string }) {
  const related = articleCards
    .filter((a) => a.slug !== currentSlug)
    .slice(0, 3)
    .map((a) => allArticles[a.slug])
    .filter(Boolean);

  return (
    <section className="w-full px-4 md:px-8 py-12 bg-background-100">
      <div className="max-w-3xl mx-auto">
        <h3 className="text-lg font-bold text-foreground-900 mb-6">Related Articles</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {related.map((article) => (
            <Link
              key={article.slug}
              to={`/${article.slug}`}
              className="group bg-background-50 rounded-lg p-4 border border-background-200/70 hover:border-primary-200 transition-colors cursor-pointer"
            >
              <img
                src={article.image}
                alt={article.imageAlt}
                className="w-full h-32 object-cover object-top rounded-md mb-3"
                loading="lazy"
              />
              <h4 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                {article.title}
              </h4>
              <p className="text-xs text-foreground-500 mt-1 line-clamp-1">{article.subtitle}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function ArticlePage({ article }: { article: ArticleData }) {
  return (
    <>
      <Navbar />
      <main>
        <article>
          <header className="w-full pt-24 pb-10 md:pt-28 md:pb-14 px-4 md:px-8 bg-background-50">
            <div className="max-w-3xl mx-auto text-center">
              <div className="flex flex-wrap items-center justify-center gap-2 mb-4">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-3 py-1 bg-secondary-100 text-secondary-800 text-xs font-medium rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 leading-tight">
                {article.title}
              </h1>
              <p className="mt-4 text-base md:text-lg text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                {article.subtitle}
              </p>
              <div className="mt-6 flex items-center justify-center gap-3 text-xs text-foreground-500">
                <span>Updated: June 2026</span>
                <span>·</span>
                <span>8 min read</span>
                <span>·</span>
                <Link to="/" className="text-primary-600 hover:text-primary-700 font-semibold cursor-pointer">
                  AllChinaBuy Spreadsheet Home
                </Link>
              </div>
            </div>
          </header>

          <div className="w-full px-4 md:px-8 pb-6 bg-background-50">
            <div className="max-w-3xl mx-auto">
              <img
                src={article.image}
                alt={article.imageAlt}
                className="w-full h-56 md:h-72 object-cover object-top rounded-lg"
                loading="eager"
              />
            </div>
          </div>

          <section className="w-full px-4 md:px-8 py-8 md:py-12 bg-background-50">
            <div className="max-w-3xl mx-auto prose-custom space-y-6 text-foreground-700 leading-relaxed">
              {article.sections.map((section, idx) => (
                <div key={idx}>
                  <h2 className="text-xl font-bold text-foreground-900 mt-8 mb-4">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((p, pIdx) => (
                    <p key={pIdx} className="mb-4" dangerouslySetInnerHTML={{ __html: p }} />
                  ))}
                  {section.listItems && (
                    <ul className="list-disc pl-6 space-y-2 mt-3 mb-4">
                      {section.listItems.map((item, lIdx) => (
                        <li key={lIdx} className="text-sm md:text-base" dangerouslySetInnerHTML={{ __html: item }} />
                      ))}
                    </ul>
                  )}
                  {section.isComparison && section.comparisonData && (
                    <div className="my-8 bg-foreground-900 rounded-lg p-5 md:p-6">
                      <h3 className="text-base font-bold text-background-50 mb-4">
                        AllChinaBuy Spreadsheet vs Other Agents
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                          <thead>
                            <tr className="border-b border-foreground-700">
                              <th className="pb-3 font-semibold text-background-50 whitespace-nowrap pr-4">Feature</th>
                              <th className="pb-3 font-semibold text-primary-300 whitespace-nowrap pr-4">With Spreadsheet</th>
                              <th className="pb-3 font-semibold text-foreground-400 whitespace-nowrap">Without</th>
                            </tr>
                          </thead>
                          <tbody>
                            {section.comparisonData.map((row) => (
                              <tr key={row.label} className="border-b border-foreground-800/50">
                                <td className="py-3 text-background-50 whitespace-nowrap pr-4 font-medium">{row.label}</td>
                                <td className="py-3 text-primary-300 whitespace-nowrap pr-4">{row.withSpreadsheet}</td>
                                <td className="py-3 text-foreground-400 whitespace-nowrap">{row.without}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          <div className="w-full px-4 md:px-8 pb-12 bg-background-50">
            <div className="max-w-3xl mx-auto pt-8 border-t border-background-200/70">
              <p className="text-xs text-foreground-500 flex flex-wrap items-center gap-2">
                <span className="font-semibold">Reviewed by Buying Experts</span>
                <span>·</span>
                <span>Last Updated: June 2026</span>
                <span>·</span>
                <span>Editorial Policy: Educational information and safe buying recommendations</span>
              </p>
              <p className="text-xs text-foreground-500 mt-1">
                <strong>Safety Disclaimer:</strong> Always verify seller reputation and product quality before purchasing.
              </p>
            </div>
          </div>

          <RelatedArticles currentSlug={article.slug} />

          <nav className="w-full px-4 md:px-8 py-10 bg-background-100">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-sm text-foreground-500 mb-3">Ready to start shopping?</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 text-base font-bold rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-home-line w-5 h-5 flex items-center justify-center"></i>
                  Back to Home
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-8 py-3 bg-foreground-900 text-background-50 text-base font-bold rounded-md hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  buy it now
                  <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
          </nav>
        </article>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              headline: article.title,
              description: article.description,
              datePublished: "2026-06-01",
              dateModified: "2026-06-25",
              author: {
                "@type": "Organization",
                name: "AllChinaBuy Spreadsheet",
                url: SITE_URL,
              },
              publisher: {
                "@type": "Organization",
                name: "AllChinaBuy Spreadsheet",
                url: SITE_URL,
              },
              mainEntityOfPage: {
                "@type": "WebPage",
                "@id": `${SITE_URL}/${article.slug}`,
              },
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: `${SITE_URL}/`,
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: article.title,
                  item: `${SITE_URL}/${article.slug}`,
                },
              ],
            }),
          }}
        />
      </main>

      <Footer />
    </>
  );
}